import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import * as api from '../services/api';

/**
 * Contexto de autenticação do criador.
 *
 * Guarda o usuário logado e expõe login/cadastro/logout para as páginas.
 * Na primeira renderização, se houver token salvo no localStorage, valida-o
 * na API (`GET /api/me`) para restaurar a sessão.
 */

interface AuthContextValue {
  /** Criador logado, ou null se não autenticado. */
  user: api.User | null;
  /** true enquanto a sessão salva ainda está sendo validada na API. */
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (payload: api.RegisterPayload) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<api.User | null>(null);
  const [isLoading, setIsLoading] = useState(() => api.getToken() !== null);

  // Restaura a sessão a partir do token salvo (se houver)
  useEffect(() => {
    if (!api.getToken()) return;

    api
      .fetchMe()
      .then(({ user: me }) => setUser(me))
      .catch(() => api.clearToken()) // token expirado/inválido: limpa
      .finally(() => setIsLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { token, user: loggedUser } = await api.login(email, password);
    api.saveToken(token);
    setUser(loggedUser);
  }, []);

  const register = useCallback(async (payload: api.RegisterPayload) => {
    const { token, user: newUser } = await api.register(payload);
    api.saveToken(token);
    setUser(newUser);
  }, []);

  const logout = useCallback(() => {
    api.clearToken();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, isLoading, login, register, logout }),
    [user, isLoading, login, register, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Hook de acesso à sessão. Use dentro de <AuthProvider>. */
// eslint-disable-next-line react-refresh/only-export-components -- padrão aceito: provider e hook do contexto vivem juntos
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de <AuthProvider>.');
  }
  return context;
}
